import { useState } from 'react';
import { NJButton, NJIconButton, NJSegmentedControl, NJSegmentedControlButton } from '@engie-group/fluid-design-system-react';
import MixedChart from '@shared/charts/MixedChart';
import { Size2MixExpandCollapseBar, Size2MixSection, Size2MixSimpleTable, useSectionsToggle } from '@shared/size2mix-shell';
import type { OptimisationDetail } from './types';

const SECTIONS = [
    { id: 'energy', label: 'Production et consommation', icon: 'factory' },
    { id: 'allocation', label: 'Répartition de la production', icon: 'tune' },
    { id: 'heat-pumps', label: 'Pompes à chaleur', icon: 'ac_unit' },
    { id: 'hydraulics', label: 'Fonctionnement hydraulique', icon: 'water' },
    { id: 'critical', label: 'Chemin critique', icon: 'route' },
    { id: 'pumps', label: 'Pompes', icon: 'settings' },
    { id: 'departures', label: 'Départs', icon: 'output' },
] as const;

export default function ChartsTab({ detail }: { detail: OptimisationDetail }) {
    const { bind, expandAll, collapseAll } = useSectionsToggle(SECTIONS.map(({ id }) => id));
    const download = () => {
        const url = URL.createObjectURL(new Blob([JSON.stringify({ optimisation: detail.summary.eid, source: 'capture DOM', seriesCaptured: false, series: [] })], { type: 'application/json' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'hiver-ete-capture.json';
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16 }}>
                <Size2MixExpandCollapseBar onExpandAll={expandAll} onCollapseAll={collapseAll} />
                <NJButton label="Télécharger les résultats" emphasis="subtle" icon="download" onClick={download} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {SECTIONS.map(({ id, label, icon }) => (
                    <Size2MixSection key={id} title={label} icon={icon} togglePosition="trailing" {...bind(id)}>
                        <ChartSection id={id} />
                    </Size2MixSection>
                ))}
            </div>
        </>
    );
}

function ChartSection({ id }: { id: string }) {
    const [view, setView] = useState('chart');
    const [group, setGroup] = useState('site');
    const [chartKey, setChartKey] = useState(0);
    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                {id === 'allocation' && <NJSegmentedControl label="hw-production-allocation-options" size="sm" value={group} onChange={setGroup}>
                    <NJSegmentedControlButton value="site" label="Site de production" /><NJSegmentedControlButton value="boiler" label="Chaudière" /><NJSegmentedControlButton value="contract" label="Contrat" />
                </NJSegmentedControl>}
                <NJSegmentedControl label="table-graph-options" size="sm" value={view} onChange={setView}>
                    <NJSegmentedControlButton value="table" label="Tableau" iconName="table_chart" /><NJSegmentedControlButton value="chart" label="Graphique" iconName="bar_chart" />
                </NJSegmentedControl>
            </div>
            {view === 'chart' ? <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24, fontSize: 14, lineHeight: '16px' }}>
                    <NJIconButton icon="refresh" label="Réinitialiser le zoom" variant="tertiary" scale="xs" onClick={() => setChartKey((key) => key + 1)} />
                    <span>Ctrl + molette pour zoomer, Ctrl + glissement pour se déplacer, click + drag pour zoomer sur une plage</span>
                </div>
                <div style={{ position: 'relative' }}>
                    <MixedChart key={chartKey} type="mixed" data={{ labels: [], datasets: [] }} heightPx={id === 'allocation' ? 516 : 350} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                    <p role="note" style={{ position: 'absolute', left: 16, top: 24, fontSize: 14 }}>Séries numériques non capturées : aucun résultat PUC EDM n'est substitué à Hiver-été.</p>
                </div>
            </> : <Size2MixSimpleTable columns={[{ key: 'step', label: 'Pas' }, { key: 'value', label: 'Valeur' }]} rows={[]} emptyLabel="Séries numériques non capturées" />}
        </>
    );
}
